import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ChangePasswordModal from './ChangePasswordModal';
import SSOUnlinkModal from './SSOUnlinkModal';
import Setup2FAModal from './Setup2FAModal';
import SettingsNav from './SettingsNav';
import ProfileSection from './ProfileSection';
import SecuritySection from './SecuritySection';
import UserPulseEmailPreferences from './UserPulseEmailPreferences';
import { GetUserData } from '../../redux/usersSlice';
import useBrowserTimezoneReport from '../../hooks/useBrowserTimezoneReport';
import { USER_SETTINGS_SECTIONS } from './userSettingsConstants';
import styles from './css/UserSettings.module.css';

const SECTION_ALIASES = {
    profile: USER_SETTINGS_SECTIONS.PROFILE,
    'edit profile': USER_SETTINGS_SECTIONS.PROFILE,
    security: USER_SETTINGS_SECTIONS.SECURITY,
    'account management': USER_SETTINGS_SECTIONS.SECURITY,
    'email-preferences': USER_SETTINGS_SECTIONS.EMAIL,
};

const normalizeSection = raw => {
    const key = (raw || '').trim().toLowerCase();
    return SECTION_ALIASES[key] || USER_SETTINGS_SECTIONS.PROFILE;
};

const UserSettings = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useSelector(state => state.user);
    const [selected, setSelected] = useState(() => normalizeSection(searchParams.get('section')));
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [showSSOUnlinkModal, setShowSSOUnlinkModal] = useState(false);
    const [showSetup2FAModal, setShowSetup2FAModal] = useState(false);
    const [mfaModalIsUpdate, setMfaModalIsUpdate] = useState(false);

    useBrowserTimezoneReport(Boolean(user?.id));

    useEffect(() => {
        setSelected(normalizeSection(searchParams.get('section')));
    }, [searchParams]);

    const handleSelectSection = sectionId => {
        setSelected(sectionId);
        setSearchParams(sectionId === USER_SETTINGS_SECTIONS.PROFILE ? {} : { section: sectionId }, { replace: true });
    };

    const handleOpenSetup2FA = isUpdate => {
        setMfaModalIsUpdate(Boolean(isUpdate));
        setShowSetup2FAModal(true);
    };

    useEffect(() => {
        const linkedStatus = searchParams.get('linked');
        const errorReason = searchParams.get('reason');

        if (linkedStatus === 'success') {
            toast.success('Microsoft account linked successfully!');
            searchParams.delete('linked');
            searchParams.delete('reason');
            setSearchParams(searchParams, { replace: true });
            dispatch(GetUserData());
        } else if (linkedStatus === 'error') {
            let errorMessage = 'Failed to link Microsoft account';
            if (errorReason === 'email_mismatch') {
                errorMessage =
                    "Your Microsoft account doesn't match your registered Lens email. Please use the correct account or contact support.";
            } else if (errorReason === 'account_already_linked') {
                errorMessage = 'This Microsoft account is already linked to another user';
            } else if (errorReason === 'invalid_code') {
                errorMessage = 'Invalid authorization code. Please try again';
            } else if (errorReason === 'network_error') {
                errorMessage = 'Network error occurred. Please try again';
            }

            toast.error(errorMessage);
            searchParams.delete('linked');
            searchParams.delete('reason');
            setSearchParams(searchParams, { replace: true });
        }
    }, [dispatch, searchParams, setSearchParams]);

    return (
        <div className={styles.settingsPage}>
            <div className={styles.settingsLayout}>
                <SettingsNav selected={selected} onSelect={handleSelectSection} />
                <div className={styles.settingsContent}>
                    {selected === USER_SETTINGS_SECTIONS.PROFILE && <ProfileSection />}
                    {selected === USER_SETTINGS_SECTIONS.SECURITY && (
                        <SecuritySection
                            onOpenChangePassword={() => setShowChangePasswordModal(true)}
                            onOpenSSOUnlink={() => setShowSSOUnlinkModal(true)}
                            onOpenSetup2FA={handleOpenSetup2FA}
                        />
                    )}
                    {selected === USER_SETTINGS_SECTIONS.EMAIL && <UserPulseEmailPreferences />}
                </div>
            </div>

            <ChangePasswordModal isOpen={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} />
            <SSOUnlinkModal isOpen={showSSOUnlinkModal} onClose={() => setShowSSOUnlinkModal(false)} />
            <Setup2FAModal
                isOpen={showSetup2FAModal}
                onClose={() => setShowSetup2FAModal(false)}
                userEmail={user?.email}
                isUpdate={mfaModalIsUpdate}
                currentMfaMethod={user?.mfa_method}
            />
        </div>
    );
};

export default UserSettings;
